import { useState } from 'react'
import { AuthService } from '../services/auth'

interface PasswordChangeDialogProps {
  isOpen: boolean
  onSuccess: () => void
  onError: (message: string) => void
  onClose: () => void
  isMandatory?: boolean
}

const PasswordChangeDialog = ({ isOpen, onSuccess, onError, onClose, isMandatory = false }: PasswordChangeDialogProps) => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [isLoading, setIsLoading] = useState(false)
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  })

  const validatePassword = (password: string): string[] => {
    const errors: string[] = []

    // Check minimum length
    if (password.length < 8) {
      errors.push('8文字以上である必要があります')
    }

    // Check for uppercase letters
    if (!/[A-Z]/.test(password)) {
      errors.push('大文字を含む必要があります')
    }

    // Check for lowercase letters
    if (!/[a-z]/.test(password)) {
      errors.push('小文字を含む必要があります')
    }

    // Check for numbers
    if (!/[0-9]/.test(password)) {
      errors.push('数字を含む必要があります')
    }

    return errors
  }

  const getPasswordStrength = (password: string): { level: number; text: string; color: string } => {
    if (password.length === 0) return { level: 0, text: '', color: 'bg-gray-200' }

    // Check if password meets all requirements
    const hasMinLength = password.length >= 8
    const hasUppercase = /[A-Z]/.test(password)
    const hasLowercase = /[a-z]/.test(password)
    const hasNumbers = /[0-9]/.test(password)
    const hasSpecialChars = /[^A-Za-z0-9]/.test(password)

    // If doesn't meet basic requirements, it's weak
    if (!hasMinLength || !hasUppercase || !hasLowercase || !hasNumbers) {
      return { level: 1, text: '弱い', color: 'bg-red-500' }
    }

    // If meets basic requirements, it's at least normal
    let score = 4 // Base score for meeting requirements
    if (password.length >= 12) score++
    if (hasSpecialChars) score++

    if (score <= 4) return { level: 2, text: '普通', color: 'bg-yellow-500' }
    return { level: 3, text: '強い', color: 'bg-green-500' }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      // Validate passwords match
      if (formData.newPassword !== formData.confirmPassword) {
        throw new Error('新しいパスワードが一致しません')
      }

      // Validate password strength
      const passwordErrors = validatePassword(formData.newPassword)
      if (passwordErrors.length > 0) {
        throw new Error(`パスワードの要件を満たしていません: ${passwordErrors.join(', ')}`)
      }

      await AuthService.changePassword(formData.currentPassword, formData.newPassword)

      // Clear form
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      })

      onSuccess()
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'パスワードの変更に失敗しました'
      onError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const passwordStrength = getPasswordStrength(formData.newPassword)

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            {isMandatory ? 'パスワード変更が必要です' : 'パスワード変更'}
          </h3>
          {!isMandatory && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        
        {isMandatory && (
          <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="text-yellow-800 text-sm">
              初回ログイン時は、セキュリティのためパスワードを変更してください。
            </div>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              現在のパスワード
            </label>
            <input
              type={showPasswords.current ? "text" : "password"}
              value={formData.currentPassword}
              onChange={(e) => setFormData({...formData, currentPassword: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              required
            />
            <label className="flex items-center mt-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={showPasswords.current}
                onChange={(e) => setShowPasswords({...showPasswords, current: e.target.checked})}
                className="mr-2"
              />
              パスワードを表示
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              新しいパスワード
            </label>
            <input
              type={showPasswords.new ? "text" : "password"}
              value={formData.newPassword}
              onChange={(e) => setFormData({...formData, newPassword: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              required
            />
            <label className="flex items-center mt-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={showPasswords.new}
                onChange={(e) => setShowPasswords({...showPasswords, new: e.target.checked})}
                className="mr-2"
              />
              パスワードを表示
            </label>

            {/* Password strength indicator */}
            {formData.newPassword && (
              <div className="mt-2">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-gray-600">パスワード強度</span>
                  <span className={`font-medium ${
                    passwordStrength.level === 1 ? 'text-red-600' :
                    passwordStrength.level === 2 ? 'text-yellow-600' : 'text-green-600'
                  }`}>
                    {passwordStrength.text}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${passwordStrength.color}`}
                    style={{ width: `${(passwordStrength.level / 3) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              新しいパスワード（確認）
            </label>
            <input
              type={showPasswords.confirm ? "text" : "password"}
              value={formData.confirmPassword}
              onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              required
            />
            <label className="flex items-center mt-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={showPasswords.confirm}
                onChange={(e) => setShowPasswords({...showPasswords, confirm: e.target.checked})}
                className="mr-2"
              />
              パスワードを表示
            </label>

            {/* Password match indicator */}
            {formData.confirmPassword && (
              <div className="mt-1 text-xs">
                {formData.newPassword === formData.confirmPassword ? (
                  <span className="text-green-600">✓ パスワードが一致しています</span>
                ) : (
                  <span className="text-red-600">✗ パスワードが一致しません</span>
                )}
              </div>
            )}
          </div>

          <div className="text-xs text-gray-600 bg-gray-50 rounded p-2">
            <strong>パスワード要件:</strong>
            <ul className="mt-1 ml-4 list-disc">
              <li>8文字以上</li>
              <li>大文字を含む</li>
              <li>小文字を含む</li>
              <li>数字を含む</li>
            </ul>
          </div>

          <div className="flex space-x-3 pt-4">
            {!isMandatory && (
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-lg font-medium"
              >
                キャンセル
              </button>
            )}
            <button
              type="submit"
              disabled={isLoading || !formData.currentPassword || !formData.newPassword || !formData.confirmPassword}
              className={`${isMandatory ? 'w-full' : 'flex-1'} bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isLoading ? '変更中...' : 'パスワード変更'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default PasswordChangeDialog